import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AuthorizedApiService } from './authorized-api.service';
import { VenueConfig, SubReviewsConfig, ReviewData, DEFAULT_REVIEW_TEXT } from '../models/FSConfig';
import { ReviewState, SubmitReviewResponse } from '../models/Order';

export interface ReviewGating {
  hide: boolean;
  showThankYou: boolean;
}

@Injectable({ providedIn: 'root' })
export class ReviewService {
  private googlePlaceId?: string;
  private googleMinRating?: number;
  private _subReviews?: SubReviewsConfig;
  private _reviewText = DEFAULT_REVIEW_TEXT;
  private _enabled = false;

  // Shared rating state used by settlement and feed components
  overallRating = 0;
  ratingFood = 0;
  ratingService = 0;
  ratingAmbiance = 0;
  ratingValue = 0;
  reviewSubmitted = false;
  saveDisabled = false;
  locked = false;

  constructor(private api: AuthorizedApiService) {}

  loadConfig(cfg: VenueConfig): void {
    this._enabled = !!cfg.review?.enabled;
    this.googlePlaceId = cfg.review?.google_place_id;
    this.googleMinRating = cfg.review?.google_min_rating;
    this._subReviews = cfg.review?.sub_reviews;
    this._reviewText = cfg.review?.review_text || DEFAULT_REVIEW_TEXT;
  }

  get subReviews(): SubReviewsConfig | undefined { return this._subReviews; }
  get showFood(): boolean { return !!this._subReviews?.rating_food; }
  get showService(): boolean { return !!this._subReviews?.rating_service; }
  get showAmbiance(): boolean { return !!this._subReviews?.rating_ambiance; }
  get showValue(): boolean { return !!this._subReviews?.rating_value; }
  get hasSubReviews(): boolean { return this.showFood || this.showService || this.showAmbiance || this.showValue; }
  get reviewText(): string { return this._reviewText; }
  get thankYouText(): string { return 'Tack för din feedback!'; }
  get expiredText(): string { return 'Tidsfristen för att lämna omdöme har gått ut'; }
  get enabled(): boolean { return this._enabled; }

  canShowGoogleReview(rating: number): boolean {
    return !!this.googlePlaceId && (this.googleMinRating == null || rating >= this.googleMinRating);
  }

  // Gating for the self-service review prompt. The server computes the
  // mode and only sets review_state when something should be shown.
  evaluateGating(reviewState: ReviewState | undefined | null): ReviewGating {
    if (!reviewState) return { hide: true, showThankYou: false };
    if (reviewState.mode === 'google_cta') return { hide: false, showThankYou: true };
    return { hide: false, showThankYou: false };
  }

  getGoogleReviewUrl(): string | null {
    if (!this.googlePlaceId) return null;
    return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(this.googlePlaceId)}`;
  }

  resetRatings(): void {
    this.overallRating = 0;
    this.ratingFood = 0;
    this.ratingService = 0;
    this.ratingAmbiance = 0;
    this.ratingValue = 0;
    this.reviewSubmitted = false;
    this.saveDisabled = false;
    this.locked = false;
  }

  fetchReview(receiptKey: string): Observable<ReviewData | null> {
    return this.api.fetchReview(receiptKey);
  }

  fetchAndApplyReview(receiptKey: string): Observable<void> {
    return new Observable(subscriber => {
      this.fetchReview(receiptKey).subscribe({
        next: review => {
          if (review?.rating) {
            this.overallRating = review.rating;
            this.ratingFood = review.rating_food;
            this.ratingService = review.rating_service;
            this.ratingAmbiance = review.rating_ambiance;
            this.ratingValue = review.rating_value;
            this.reviewSubmitted = true;
            if (review.locked) {
              this.saveDisabled = true;
              this.locked = true;
            }
          }
          subscriber.next();
          subscriber.complete();
        },
        error: e => {
          console.warn('Failed to fetch review:', e);
          subscriber.next();
          subscriber.complete();
        }
      });
    });
  }

  submitReview(params: {
    date: string;
    type: 'qr_code' | 'self_service';
    rating: number;
    rating_food: number;
    rating_service: number;
    rating_ambiance: number;
    rating_value: number;
    receipt_key: string;
  }): Observable<SubmitReviewResponse> {
    return this.api.submitReview(params);
  }

  submitCurrentReview(receiptKey: string, type: 'qr_code' | 'self_service', rating: number): Observable<SubmitReviewResponse> {
    return this.submitReview({
      date: new Date().toISOString(),
      type,
      rating,
      rating_food: this.ratingFood,
      rating_service: this.ratingService,
      rating_ambiance: this.ratingAmbiance,
      rating_value: this.ratingValue,
      receipt_key: receiptKey
    });
  }
}
